import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { ReactNode, useEffect, useState } from "react"
import { Input } from "./input";
import { Clapperboard, Disc3, Dot, Mic2, Music2, Tv, User } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { SearchSpotify, SearchTMDB, SearchUsers } from "../ApiRoutes";
import { ScrollArea } from "./scroll-area";
import { SecondaryLoader } from "@/utils/Loaders";
import { ItemDetails } from "../ListViewer";
import { Avatar, AvatarFallback, AvatarImage } from "./avatar";
import { useNavigate } from "react-router-dom";

interface SearchDialogProps {
    children:ReactNode;
}

export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export default function SearchDialog({children}:SearchDialogProps){
    const [searchType, setSearchType] = useState<"movies"|"shows"|"artists"|"albums"|"songs"|"users">("users")
    const [query, setQuery] = useState<String>();
    const debouncedQuery = useDebounce(query, 500);
    const navigate = useNavigate()
    const [isOpen,setIsOpen] = useState<boolean>();

    const searchFunctions = {
        users: SearchUsers,
        movies: SearchTMDB,
        shows: SearchTMDB,
        songs: SearchSpotify,
        albums: SearchSpotify,
        artists: SearchSpotify
        
        };

    useEffect(()=>{setQuery("")},[isOpen])
    

    const { data, isPending, isLoading, isError, error, isSuccess } = useQuery({
        queryKey: ['searchResults', searchType, debouncedQuery],
        queryFn: () => searchFunctions[searchType](debouncedQuery, searchType),

        enabled: !!debouncedQuery,
    });


    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger className="">{children}</DialogTrigger>
            <DialogContent className="border-none bg-black/60 backdrop-blur-md dark border-0 text-white max-h-full ease-in-out duration-200 rounded-md p-4">
        
                <DialogHeader className="hidden text-left mb-4">
                    <DialogTitle>Search the seas</DialogTitle>
                    <DialogDescription>
                        Use the toggles below the search bar to change search type
                    </DialogDescription>
                </DialogHeader>


                <article className="flex flex-col gap-4 pt-7 border-b-2 border-neutral-700">
                    <Input className="text-white" placeholder={`Search ${searchType}`} onChange={e => setQuery(e.currentTarget.value.trim())}/>
                    <div className="flex gap-2 justify-center" >
                        {searchType == "users" ? 
                        <div className="flex flex-col"><User /><Dot/></div> : <User className="hover:cursor-pointer" onClick={() => {setSearchType("users")}}/>}
                        {searchType == "movies" ? 
                        <div className="flex flex-col"><Clapperboard /><Dot/></div> : <Clapperboard className="hover:cursor-pointer" onClick={() => {setSearchType("movies")}}/>}
                        {searchType == "shows" ? 
                        <div className="flex flex-col"><Tv /><Dot/></div> : <Tv className="hover:cursor-pointer" onClick={() => {setSearchType("shows")}}/>}
                        {searchType == "songs" ? 
                        <div className="flex flex-col"><Music2 /><Dot/></div> : <Music2 className="hover:cursor-pointer" onClick={() => {setSearchType("songs")}}/>}
                        {searchType == "albums" ? 
                        <div className="flex flex-col"><Disc3 /><Dot/></div> : <Disc3 className="hover:cursor-pointer" onClick={() => {setSearchType("albums")}}/>}
                        {searchType == "artists" ? 
                        <div className="flex flex-col"><Mic2 /><Dot/></div> : <Mic2 className="hover:cursor-pointer" onClick={() => {setSearchType("artists")}}/>}    </div>
                </article>

                { isPending && !!debouncedQuery &&
                    <section className="flex pb-7">
                        <SecondaryLoader />
                    </section>
                    
                }
                { isError && searchType == "users" && String(error.message).includes("404") &&
                    <section className="flex flex-col items-center">
                        <h3 className="text-white">
                            No users found
                        </h3>
                        <p className="text-sm text-neutral-500">
                            ({error.message})
                        </p>
                    </section>
                    
                }

                { isError && searchType != "users" &&
                    <section className="flex flex-col items-center">
                        <h3 className="text-white">
                            An error has occured
                        </h3>
                        <p className="text-sm text-neutral-500">
                            We may be expierencing issues, try again later.
                        </p>
                    </section>
                    
                }
                { isSuccess && data.length < 1 &&
                    <section className="flex flex-col items-center">
                        <h3 className="text-white">
                            No results found
                        </h3>
                        <p className="text-sm text-neutral-500">
                           Might be a typo?
                        </p>
                    </section>
                }

                { data && isSuccess && searchType != "users" && // users have different attribute names
                <ScrollArea className="flex flex-col h-60"> 
                    
                    { data.filter(item => {
                        return item.poster_path || item.album?.images?.[0]?.url || item.images?.[0]?.url;
                    }).map((item:object, index:number) => 
                    <ItemDetails id={item.id} itemType={searchType}>
                        <article key={item.id} className="flex ease-in-out duration-200 mb-2 h-25 gap-4 hover:cursor-pointer hover:bg-black/40">
                            <img className="rounded-lg" src={`${searchType == "shows" || searchType == "movies" ? "https://image.tmdb.org/t/p/w500/"+item.poster_path : ( searchType == "songs" ? item.album.images[0].url : item.images[0].url )}  `}/>
                            <div className="flex flex-col items-center justify-center">
                                <h4 className="text-sm font-semibold">{item.title ?? item.name}</h4>
                            </div>
                        </article>
                    </ItemDetails>
                    )}
                </ScrollArea>
                }

                { data && isSuccess && searchType == "users" && 
                    <ScrollArea className="flex flex-col max-h-50">
                        {data.map((item,index) => 
                            <article key={item.id} onClick={() => {navigate(`/profile/${item.username}`); setIsOpen(false); setQuery("");}} className="flex items-center px-5 ease-in-out duration-200 mb-2 h-20 gap-4 hover:cursor-pointer hover:bg-black/40">
                                <Avatar className="h-15 w-15 border-2 border-white text-black">
                                    <AvatarImage draggable={false} src={item.profile.avatar ? "https://bay-1.tor1.cdn.digitaloceanspaces.com/media/"+item.profile.avatar : ""} />
                                    <AvatarFallback>:)</AvatarFallback>
                                </Avatar>
                                    <h4 className="text-base font-semibold">{item.username}</h4>
                            </article>
                        )}
                    </ScrollArea>
                }
            </DialogContent>
        </Dialog>

    )
}


